/**
 * Menu theo role – POC và triển khai thật dùng chung.
 * Đương sự (user/student/litigant) | Thư ký (secretary) | Thẩm phán (judge) | Chánh án (admin/chief_judge)
 */
import type { ReactNode } from 'react'
import {
  Folder,
  AddCircle,
  UploadFile,
  Dashboard,
  CalendarMonth,
  Assessment,
  Person,
  Assignment,
  HowToReg,
  AutoAwesome,
  Gavel,
  RateReview,
} from '@mui/icons-material'

export interface MenuSubItem {
  id: string
  text: string
  path: string
}

export interface MenuItem {
  text: string
  icon: ReactNode
  path: string
  roles?: string[] // undefined = mọi role đều thấy (Trang chủ, Profile)
  /** Sub-menu (navy tone) khi vào đúng path. */
  children?: MenuSubItem[]
  /** Nhóm Công cụ AI: luôn expand khi pathname bắt đầu /workspace/ai- */
  isAIGroup?: boolean
}

const preliminarySubMenu: MenuSubItem[] = [
  { id: 'new', text: 'Đơn mới tiếp nhận', path: '/workspace/preliminary?tab=new' },
  { id: 'processing', text: 'Đang xử lý', path: '/workspace/preliminary?tab=processing' },
  { id: 'archive', text: 'Tài liệu lưu trữ', path: '/workspace/preliminary?tab=archive' },
  { id: 'search', text: 'Tìm kiếm', path: '/workspace/preliminary?tab=search' },
]

export const menuItems: MenuItem[] = [
  { text: 'Bảng điều khiển', icon: <Dashboard />, path: '/workspace', roles: ['secretary', 'judge', 'admin', 'chief_judge'] },
  { text: 'Vụ án của tôi', icon: <Folder />, path: '/cases', roles: ['user', 'student', 'litigant', 'secretary', 'judge', 'admin', 'chief_judge'] },
  { text: 'Nộp đơn khởi kiện', icon: <AddCircle />, path: '/cases/new', roles: ['user', 'student', 'litigant'] },
  { text: 'Giao nộp chứng cứ', icon: <UploadFile />, path: '/cases/evidence', roles: ['user', 'student', 'litigant'] },
  { text: 'Xử lý sơ bộ', icon: <Assignment />, path: '/workspace/preliminary', roles: ['secretary'], children: preliminarySubMenu },
  { text: 'Danh sách án', icon: <Folder />, path: '/workspace/cases', roles: ['secretary', 'judge', 'admin', 'chief_judge'] },
  { text: 'Lịch làm việc', icon: <CalendarMonth />, path: '/workspace/calendar', roles: ['secretary', 'judge', 'admin', 'chief_judge'] },
  { text: 'Văn bản chờ duyệt / Ký', icon: <RateReview />, path: '/workspace/documents-pending', roles: ['judge', 'admin', 'chief_judge'] },
  { text: 'Quyết định thẩm quyền', icon: <Gavel />, path: '/workspace/preliminary-chief', roles: ['admin', 'chief_judge'] },
  { text: 'Phân công án', icon: <HowToReg />, path: '/workspace/assign', roles: ['admin', 'chief_judge'] },
  { text: 'Báo cáo', icon: <Assessment />, path: '/workspace/reports', roles: ['admin', 'chief_judge'] },
  {
    text: 'Công cụ AI',
    icon: <AutoAwesome />,
    path: '/workspace/ai-draft',
    roles: ['secretary', 'judge', 'admin', 'chief_judge'],
    isAIGroup: true,
    children: [
      { id: 'ai-draft', text: 'Soạn thảo văn bản', path: '/workspace/ai-draft' },
      { id: 'ai-evidence', text: 'Chứng cứ', path: '/workspace/ai-evidence' },
      { id: 'ai-research', text: 'Research (Pháp luật & Án lệ)', path: '/workspace/ai-research' },
    ],
  },
  { text: 'Thông tin cá nhân', icon: <Person />, path: '/profile' },
]

/** Menu riêng cho Chánh án: Bảng điều khiển, Quyết định thẩm quyền, Văn bản chờ ký, Phân công án, Báo cáo, Công cụ AI, Hồ sơ. */
const CHIEF_JUDGE_PATHS = ['/workspace', '/workspace/preliminary-chief', '/workspace/documents-pending', '/workspace/assign', '/workspace/reports', '/workspace/ai-draft', '/workspace/ai-evidence', '/workspace/ai-research', '/profile']

export function getMenuItemsForRole(role: string): MenuItem[] {
  const isChiefJudge = role === 'admin' || role === 'chief_judge'
  const isLitigant = ['user', 'litigant', 'student'].includes(role)
  if (isChiefJudge) {
    return menuItems.filter(
      (item) =>
        CHIEF_JUDGE_PATHS.includes(item.path) ||
        (item.isAIGroup && item.children?.some((c) => CHIEF_JUDGE_PATHS.includes(c.path)))
    )
  }
  return menuItems.filter((item) => !item.roles || item.roles.includes(role))
}
